<template>
  <iframe 
    ref="messageFrame"
    frameborder="0" 
    :src="src"
    :style="{ minWidth: '100%', width: '1px', height: iframeHeight + 'px' }"
    @error="handleIframeError"
    @load="onIframeLoad"
  >
  </iframe>
</template>

<script>
// Height constraints for iframe
const MIN_IFRAME_HEIGHT = 50
const MAX_IFRAME_HEIGHT = 800

export default {
  name: "MessageFrame",
  props: {
    messageId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      iframeHeight: MIN_IFRAME_HEIGHT, // Default minimum height
      resizeObserver: null
    }
  },
  computed: {
    src() {
      const isLocalhost = window.location.hostname.includes("localhost") || window.location.hostname.includes("192.168")
      const token = window.localStorage.session
      
      // For localhost, use proxy endpoint to avoid cross-origin issues
      // For production/staging, go directly to API via haproxy (no proxy needed)
      const path = isLocalhost 
        ? "/proxy/commsroom/messages/" + this.messageId + "/body?token=" + token
        : "/commsroom/messages/" + this.messageId + "/body?token=" + token
      
      return path
    }
  },
  mounted() {
    // Error handling will be set up when iframe loads
  },
  watch: {
    messageId(newId, oldId) {
      console.debug('[MessageFrame] messageId changed:', { from: oldId, to: newId })
      // Reset height when message changes
      this.iframeHeight = MIN_IFRAME_HEIGHT
      console.debug(`[MessageFrame] Reset iframeHeight to default ${MIN_IFRAME_HEIGHT}px`)
      // Clean up old observer
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
        console.debug('[MessageFrame] Disconnected old resize observer')
      }
      // Height will be remeasured when new iframe loads
    }
  },
  beforeDestroy() {
    // Clean up resize observer if it exists
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    handleIframeError(event) {
      // Silently handle iframe load errors (network/loading errors)
      // Note: JavaScript syntax errors in iframe content are caught by the global error handler
      // Don't log to avoid console noise
    },
    onIframeLoad() {
      console.debug('[MessageFrame] onIframeLoad: iframe loaded for messageId:', this.messageId)
      console.debug('[MessageFrame] onIframeLoad: Current iframeHeight:', this.iframeHeight)
      
      // Measure and set iframe height when content loads
      this.measureAndSetHeight()
      
      // Set up periodic checking in case content changes
      // Also try to set up a resize observer if possible
      this.setupHeightMonitoring()
      
      // Set up error handling for iframe content
      this.setupIframeErrorHandling()
    },
    measureAndSetHeight() {
      const iframe = this.$refs.messageFrame
      if (!iframe) {
        console.debug('[MessageFrame] measureAndSetHeight: iframe ref not found')
        return
      }

      console.debug('[MessageFrame] measureAndSetHeight: Starting height calculation for messageId:', this.messageId)

      // Log origin information
      const currentOrigin = window.location.origin
      const iframeSrc = iframe.src || this.src
      
      // Try to extract origin from src URL
      let iframeOriginFromUrl = null
      try {
        if (iframeSrc) {
          const url = new URL(iframeSrc, window.location.href)
          iframeOriginFromUrl = url.origin
        }
      } catch (urlError) {
        console.debug('[MessageFrame] measureAndSetHeight: Could not parse iframe src URL:', urlError.message)
      }
      
      console.debug('[MessageFrame] measureAndSetHeight: Origin information:', {
        currentWindowOrigin: currentOrigin,
        iframeSrc: iframeSrc,
        iframeSrcAttribute: iframe.getAttribute('src'),
        iframeOriginFromUrl: iframeOriginFromUrl,
        originsMatch: iframeOriginFromUrl === currentOrigin,
        isCrossOrigin: iframeOriginFromUrl !== null && iframeOriginFromUrl !== currentOrigin
      })

      try {
        // Try to access iframe content to measure its height
        const iframeWindow = iframe.contentWindow
        
        // Try to get iframe origin (will throw SecurityError if cross-origin)
        let iframeOrigin = null
        try {
          if (iframeWindow && iframeWindow.location) {
            iframeOrigin = iframeWindow.location.origin
            console.debug('[MessageFrame] measureAndSetHeight: iframe origin accessible:', iframeOrigin)
          }
        } catch (originError) {
          console.debug('[MessageFrame] measureAndSetHeight: Cannot access iframe origin (cross-origin):', {
            errorName: originError.name,
            errorMessage: originError.message,
            iframeSrc: iframeSrc
          })
        }
        
        const iframeDocument = iframe.contentDocument || (iframeWindow && iframeWindow.document)
        
        if (!iframeDocument) {
          // Cross-origin or not accessible - keep default height
          console.debug('[MessageFrame] measureAndSetHeight: iframeDocument not accessible (cross-origin or not loaded)')
          console.debug('[MessageFrame] measureAndSetHeight: iframeWindow:', iframeWindow ? 'exists' : 'null')
          console.debug('[MessageFrame] measureAndSetHeight: Origin comparison:', {
            currentOrigin,
            iframeOrigin: iframeOrigin || 'not accessible',
            originsMatch: iframeOrigin === currentOrigin,
            isCrossOrigin: iframeOrigin !== null && iframeOrigin !== currentOrigin
          })
          console.debug(`[MessageFrame] measureAndSetHeight: Keeping default height of ${MIN_IFRAME_HEIGHT}px`)
          return
        }

        console.debug('[MessageFrame] measureAndSetHeight: iframeDocument accessible')

        // Get the body or document element height
        const body = iframeDocument.body
        const html = iframeDocument.documentElement
        
        const bodyScrollHeight = (body && body.scrollHeight) || 0
        const bodyOffsetHeight = (body && body.offsetHeight) || 0
        const htmlScrollHeight = (html && html.scrollHeight) || 0
        const htmlClientHeight = (html && html.clientHeight) || 0
        
        console.debug('[MessageFrame] measureAndSetHeight: Height measurements:', {
          bodyScrollHeight,
          bodyOffsetHeight,
          htmlScrollHeight,
          htmlClientHeight,
          bodyExists: !!body,
          htmlExists: !!html
        })
        
        // Get the maximum height from body scrollHeight, body offsetHeight, or html clientHeight
        const contentHeight = Math.max(
          bodyScrollHeight,
          bodyOffsetHeight,
          htmlScrollHeight,
          htmlClientHeight
        )

        console.debug('[MessageFrame] measureAndSetHeight: Calculated contentHeight:', contentHeight)

        // Apply min/max constraints
        const constrainedHeight = Math.max(MIN_IFRAME_HEIGHT, Math.min(MAX_IFRAME_HEIGHT, contentHeight))
        
        console.debug('[MessageFrame] measureAndSetHeight: Final constrainedHeight:', constrainedHeight, {
          wasClampedToMin: contentHeight < MIN_IFRAME_HEIGHT,
          wasClampedToMax: contentHeight > MAX_IFRAME_HEIGHT,
          originalContentHeight: contentHeight
        })
        
        this.iframeHeight = constrainedHeight
        console.debug('[MessageFrame] measureAndSetHeight: Set iframeHeight to:', this.iframeHeight)
      } catch (e) {
        // Cross-origin or other access errors - keep default height
        // This is expected for cross-origin iframes
        console.debug('[MessageFrame] measureAndSetHeight: Error accessing iframe content:', e)
        console.debug('[MessageFrame] measureAndSetHeight: Error details:', {
          errorName: e.name,
          errorMessage: e.message,
          errorStack: e.stack,
          currentOrigin: currentOrigin,
          iframeSrc: iframeSrc,
          iframeOriginFromUrl: iframeOriginFromUrl,
          isCrossOrigin: iframeOriginFromUrl !== null && iframeOriginFromUrl !== currentOrigin
        })
        
        // Try to determine iframe origin by accessing iframeWindow.location (will fail if cross-origin)
        try {
          const iframeWindow = iframe.contentWindow
          if (iframeWindow && iframeWindow.location) {
            const iframeOrigin = iframeWindow.location.origin
            console.debug('[MessageFrame] measureAndSetHeight: iframe origin (accessible from iframeWindow.location):', iframeOrigin)
          }
        } catch (originError) {
          console.debug('[MessageFrame] measureAndSetHeight: Cannot access iframeWindow.location.origin (cross-origin restriction):', {
            errorName: originError.name,
            errorMessage: originError.message,
            iframeOriginFromUrl: iframeOriginFromUrl || 'could not parse from URL'
          })
        }
        
        console.debug(`[MessageFrame] measureAndSetHeight: Keeping default height of ${MIN_IFRAME_HEIGHT}px due to error`)
      }
    },
    setupHeightMonitoring() {
      const iframe = this.$refs.messageFrame
      if (!iframe) {
        console.debug('[MessageFrame] setupHeightMonitoring: iframe ref not found')
        return
      }

      try {
        const iframeWindow = iframe.contentWindow
        const iframeDocument = iframe.contentDocument || (iframeWindow && iframeWindow.document)
        if (!iframeDocument) {
          console.debug('[MessageFrame] setupHeightMonitoring: iframeDocument not accessible, cannot set up monitoring')
          return
        }

        console.debug('[MessageFrame] setupHeightMonitoring: Setting up MutationObserver and resize listener')

        // Set up a MutationObserver to watch for content changes
        const observer = new MutationObserver(() => {
          console.debug('[MessageFrame] setupHeightMonitoring: MutationObserver triggered, remeasuring height')
          this.measureAndSetHeight()
        })

        observer.observe(iframeDocument.body || iframeDocument.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        })

        // Store observer for cleanup
        this.resizeObserver = observer
        console.debug('[MessageFrame] setupHeightMonitoring: MutationObserver set up successfully')

        // Also listen for resize events on the iframe window
        if (iframeWindow) {
          iframeWindow.addEventListener('resize', () => {
            console.debug('[MessageFrame] setupHeightMonitoring: Resize event triggered, remeasuring height')
            this.measureAndSetHeight()
          })
          console.debug('[MessageFrame] setupHeightMonitoring: Resize event listener added')
        }
      } catch (e) {
        // Cross-origin or other access errors - can't monitor
        console.debug('[MessageFrame] setupHeightMonitoring: Error setting up monitoring:', e)
        console.debug('[MessageFrame] setupHeightMonitoring: Error name:', e.name, 'Error message:', e.message)
      }
    },
    setupIframeErrorHandling() {
      // Try to catch errors from within the iframe
      const iframe = this.$refs.messageFrame
      if (!iframe) return

      try {
        // Try to access iframe content to set up error handler
        // This may fail due to same-origin policy, which is fine
        const iframeWindow = iframe.contentWindow
        if (iframeWindow) {
          // Override the iframe's error handler to catch syntax errors
          const originalOnError = iframeWindow.onerror
          iframeWindow.onerror = (message, source, lineno, colno, error) => {
            // Check if this is an email body syntax error
            if (source && (
              source.includes('/body?token=') ||
              source.includes('/commsroom/messages/') ||
              (message && (
                String(message).includes('Invalid or unexpected token') ||
                String(message).includes('missing )') ||
                String(message).includes('Unexpected token')
              ))
            )) {
              // Silently catch these errors - they're from email content
              return true // Prevent error from propagating
            }
            // For other errors, call original handler if it exists
            if (originalOnError) {
              return originalOnError.call(iframeWindow, message, source, lineno, colno, error)
            }
            return false
          }
        }
      } catch (e) {
        // Cross-origin or other access errors - this is expected
        // The global error handler will catch errors that bubble up
      }
    }
  }
}
</script>
